import { clientConfig } from '@/config/clientConfig';
import type { BulkActionResponse } from '@/domains/jobs/api/workOrdersApi';
import { BulkActionResultModal } from '@/domains/jobs/components/BulkActionResultModal';
import { BulkActionStepperModal } from '@/domains/jobs/components/BulkActionStepperModal';
import type {
  BulkActionMode,
  BulkActionStepperSubmitItem,
} from '@/domains/jobs/components/BulkActionStepperModal';
import { BulkActionsBar } from '@/domains/jobs/components/BulkActionsBar';
import { RequestMoreInfoOrReplyModal } from '@/domains/jobs/components/RequestMoreInfoOrReplyModal';
import type { BulkJobInput } from '@/domains/jobs/hooks/useWorkOrderActions';
import { PageHeader } from '@/shared/components/PageHeader';
import { showSuccessToast } from '@/shared/notifications/toast-utils';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import {
  AlertCircle,
  ArrowDownLeft,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileText,
  HelpCircle,
  ShieldCheck,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useJobList } from '../hooks/useJobList';
import {
  useAcceptWorkOrders,
  useRejectWorkOrders,
  useReplyMoreInfoWorkOrders,
  useRequestMoreInfoWorkOrders,
} from '../hooks/useWorkOrderActions';
import { WorkOrdersFilters } from './WorkOrdersFilters';

type Priority = 'Critical' | 'High' | 'Medium' | 'Low';

export interface WorkOrder {
  id: number;
  name: string | null;
  job_category_id: number | null;
  status: number | null;
  priority: string | null;
  priority_id: number | null;
  priority_name: string | null;
  address: string | null;
  req_completion_date: string | null;
  created_time: string | null;
  start_date: string | null;
  end_date: string | null;
  work_order_status_id: number | null;
  work_order_status: string | null;
  permit_required: boolean;
  reinstatement_required: boolean;
  permit_status_id: number | null;
  permit_status_name: string | null;
  job_category_name: string | null;
  mir_pending_direction: 'inbound' | 'outbound' | null;
  external_id: string | null;
}

export interface ConversationMessage {
  id: string;
  type: 'request-info' | 'response' | 'rejection' | 'resent';
  sender: 'skewbworks' | 'core';
  timestamp: string;
  message: string;
  createdBy?: string | null;
  eventType?: string;
  files?: { name: string; url?: string }[];
}

const priorityOrder: Record<Priority, number> = {
  Critical: 0,
  High: 1,
  Medium: 2,
  Low: 3,
};

const priorityColors: Record<Priority, string> = {
  Critical: 'text-red-600 border-red-600',
  High: 'text-red-600 border-red-600',
  Medium: 'text-amber-600 border-amber-600',
  Low: 'text-slate-600 border-slate-600',
};

const FILTERS_SESSION_KEY = 'work-orders-filters';

function loadFiltersFromSession(): Record<string, string | undefined> {
  try {
    const stored = sessionStorage.getItem(FILTERS_SESSION_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function saveFiltersToSession(filters: Record<string, string | undefined>) {
  sessionStorage.setItem(FILTERS_SESSION_KEY, JSON.stringify(filters));
}

interface WorkOrdersListProps {
  onNavigateToDetail?: (orderId: string) => void;
}

export function WorkOrdersList({ onNavigateToDetail }: WorkOrdersListProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Restore filters: URL params take priority, then sessionStorage, then 'all'
  const sessionFilters = loadFiltersFromSession();
  const hasUrlFilters =
    searchParams.has('priority_id') ||
    searchParams.has('job_category_id') ||
    searchParams.has('work_order_status_id') ||
    searchParams.has('search_term') ||
    searchParams.has('mir_pending_direction');

  const [searchTerm, setSearchTerm] = useState(
    searchParams.get('search_term') ?? sessionFilters.search_term ?? '',
  );
  const [priorityFilter, setPriorityFilter] = useState<string>(
    searchParams.get('priority_id') ?? sessionFilters.priority_id ?? 'all',
  );
  const [jobTypeFilter, setJobTypeFilter] = useState<string>(
    searchParams.get('job_category_id') ?? sessionFilters.job_category_id ?? 'all',
  );
  const [workOrderStatusFilter, setWorkOrderStatusFilter] = useState<string>(
    searchParams.get('work_order_status_id') ?? sessionFilters.work_order_status_id ?? 'all',
  );
  const [mirPendingDirectionFilter, setMirPendingDirectionFilter] = useState<string>(
    searchParams.get('mir_pending_direction') ?? sessionFilters.mir_pending_direction ?? 'all',
  );
  const [currentPage, setCurrentPageState] = useState<number>(
    parseInt(searchParams.get('page') ?? '1', 10),
  );
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [stepperMode, setStepperMode] = useState<BulkActionMode | null>(null);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [requestMoreInfoModalOpen, setRequestMoreInfoModalOpen] = useState(false);
  const [resultModalState, setResultModalState] = useState<
    | {
        open: true;
        actionLabel: string;
        results: BulkActionResponse['results'];
        jobsLookup: { id: number; name: string | null }[];
      }
    | { open: false }
  >({ open: false });

  const acceptWorkOrders = useAcceptWorkOrders();
  const rejectWorkOrders = useRejectWorkOrders();
  const requestMoreInfoWorkOrders = useRequestMoreInfoWorkOrders();
  const replyMoreInfoWorkOrders = useReplyMoreInfoWorkOrders();

  // On mount: if URL has no params but session has filters, restore them to URL
  useEffect(() => {
    if (!hasUrlFilters) {
      const next = new URLSearchParams();
      if (sessionFilters.priority_id && sessionFilters.priority_id !== 'all')
        next.set('priority_id', sessionFilters.priority_id);
      if (sessionFilters.job_category_id && sessionFilters.job_category_id !== 'all')
        next.set('job_category_id', sessionFilters.job_category_id);
      if (sessionFilters.work_order_status_id && sessionFilters.work_order_status_id !== 'all')
        next.set('work_order_status_id', sessionFilters.work_order_status_id);
      if (sessionFilters.search_term && sessionFilters.search_term !== '')
        next.set('search_term', sessionFilters.search_term);
      if (sessionFilters.mir_pending_direction && sessionFilters.mir_pending_direction !== 'all')
        next.set('mir_pending_direction', sessionFilters.mir_pending_direction);
      if (next.toString()) {
        setSearchParams(next, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyFilters = (values: {
    priority: string;
    jobType: string;
    status: string;
    mir: string;
    searchTerm: string;
  }) => {
    setPriorityFilter(values.priority);
    setJobTypeFilter(values.jobType);
    setWorkOrderStatusFilter(values.status);
    setMirPendingDirectionFilter(values.mir);
    setSearchTerm(values.searchTerm);
    setCurrentPageState(1);
    setSelectedOrders(new Set());
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        values.priority !== 'all'
          ? next.set('priority_id', values.priority)
          : next.delete('priority_id');
        values.jobType !== 'all'
          ? next.set('job_category_id', values.jobType)
          : next.delete('job_category_id');
        values.status !== 'all'
          ? next.set('work_order_status_id', values.status)
          : next.delete('work_order_status_id');
        values.mir !== 'all'
          ? next.set('mir_pending_direction', values.mir)
          : next.delete('mir_pending_direction');
        values.searchTerm !== ''
          ? next.set('search_term', values.searchTerm)
          : next.delete('search_term');
        next.delete('page');
        return next;
      },
      { replace: true },
    );
    saveFiltersToSession({
      priority_id: values.priority !== 'all' ? values.priority : undefined,
      job_category_id: values.jobType !== 'all' ? values.jobType : undefined,
      work_order_status_id: values.status !== 'all' ? values.status : undefined,
      mir_pending_direction: values.mir !== 'all' ? values.mir : undefined,
      search_term: values.searchTerm !== '' ? values.searchTerm : undefined,
    });
  };

  const handleResetFilters = () => {
    setPriorityFilter('all');
    setJobTypeFilter('all');
    setWorkOrderStatusFilter('all');
    setMirPendingDirectionFilter('all');
    setSearchTerm('');
    setCurrentPageState(1);
    setSelectedOrders(new Set());
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('priority_id');
        next.delete('job_category_id');
        next.delete('work_order_status_id');
        next.delete('mir_pending_direction');
        next.delete('search_term');
        next.delete('page');
        return next;
      },
      { replace: true },
    );
    saveFiltersToSession({});
  };

  const handleSearchTermChange = (value: string) => {
    setSearchTerm(value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === '') {
          next.delete('search_term');
        } else {
          next.set('search_term', value);
        }
        next.delete('page');
        return next;
      },
      { replace: true },
    );
    setCurrentPageState(1);
    // See updateFilter — same reasoning: keep selection coherent with view.
    setSelectedOrders(new Set());
    const current = loadFiltersFromSession();
    if (value === '') {
      current['search_term'] = undefined;
    } else {
      current['search_term'] = value;
    }
    saveFiltersToSession(current);
  };

  const limit = 50;

  const setCurrentPage = (updater: number | ((prev: number) => number)) => {
    const newPage = typeof updater === 'function' ? updater(currentPage) : updater;
    setCurrentPageState(newPage);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (newPage === 1) {
          next.delete('page');
        } else {
          next.set('page', String(newPage));
        }
        return next;
      },
      { replace: true },
    );
  };

  // Fetch job list from backend with optional filter params
  const { data: jobListData, refetch: refetchJobList } = useJobList({
    limit,
    skip: (currentPage - 1) * limit,
    priority_id: priorityFilter !== 'all' ? parseInt(priorityFilter, 10) : undefined,
    job_category_id: jobTypeFilter !== 'all' ? parseInt(jobTypeFilter, 10) : undefined,
    work_order_status_id:
      workOrderStatusFilter !== 'all' ? parseInt(workOrderStatusFilter, 10) : undefined,
    search_term: searchTerm !== '' ? searchTerm : undefined,
    mir_pending_direction:
      mirPendingDirectionFilter !== 'all'
        ? (mirPendingDirectionFilter as 'inbound' | 'outbound' | 'none')
        : undefined,
  });

  const workOrders = useMemo((): WorkOrder[] => jobListData?.data ?? [], [jobListData]);
  const totalCount = jobListData?.total ?? 0;
  const totalPages = Math.ceil(totalCount / limit);

  // Filter and sort orders
  const filteredOrders = useMemo(() => {
    const filtered = [...workOrders];

    // Sort by priority (Critical > High > Medium > Low) then by newest
    filtered.sort((a, b) => {
      const priorityCompare =
        priorityOrder[(a.priority_name ?? 'Low') as Priority] -
        priorityOrder[(b.priority_name ?? 'Low') as Priority];
      if (priorityCompare !== 0) return priorityCompare;

      // If same priority, sort by newest (created_time descending)
      return new Date(b.created_time ?? '').getTime() - new Date(a.created_time ?? '').getTime();
    });

    return filtered;
  }, [workOrders]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedOrders(new Set(filteredOrders.map((o) => String(o.id))));
    } else {
      setSelectedOrders(new Set());
    }
  };

  const handleSelectOrder = (orderId: string, checked: boolean) => {
    const newSelected = new Set(selectedOrders);
    if (checked) {
      newSelected.add(orderId);
    } else {
      newSelected.delete(orderId);
    }
    setSelectedOrders(newSelected);
  };

  const handleAccept = () => setAcceptModalOpen(true);
  const handleReject = () => setStepperMode('reject');
  const handleRequestInfo = () => setRequestMoreInfoModalOpen(true);

  const allSelected =
    filteredOrders.length > 0 && filteredOrders.every((o) => selectedOrders.has(String(o.id)));

  // Full job objects for the currently-selected IDs (used by the bulk bar to
  // derive status, by the stepper to render per-job context, and by the result
  // modal to resolve IDs back to names).
  const selectedJobs = useMemo(
    () => filteredOrders.filter((o) => selectedOrders.has(String(o.id))),
    [filteredOrders, selectedOrders],
  );

  /**
   * Handle the per-job results array returned by any bulk action.
   * - All succeeded → green toast; no modal
   * - Any failed → open the result modal with the mixed/failed breakdown
   * Also clears the current selection + closes any open bulk-action UI.
   */
  const handleBulkResult = (response: BulkActionResponse, actionLabel: string) => {
    const results = response.results ?? [];
    const failed = results.filter((r) => !r.success);
    const lookup = selectedJobs.map((j) => ({ id: j.id, name: j.name }));

    if (failed.length === 0) {
      const n = results.length;
      showSuccessToast(`${n} job${n === 1 ? '' : 's'} ${actionLabel.toLowerCase()}`);
      setSelectedOrders(new Set());
      setStepperMode(null);
      setAcceptModalOpen(false);
      return;
    }

    setResultModalState({
      open: true,
      actionLabel,
      results,
      jobsLookup: lookup,
    });
    // On partial failure we still clear the selection and close the stepper —
    // the user reads the result modal to see which ones failed and can
    // re-select them after closing.
    setSelectedOrders(new Set());
    setStepperMode(null);
    setAcceptModalOpen(false);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <PageHeader
        title="Jobs"
        subtitle={`${jobListData?.total ?? 0} total jobs • Back Office Management`}
        icon={<FileText className="w-7 h-7 text-orange-600 dark:text-orange-400" />}
        breadcrumbs={[{ label: 'Jobs', current: true }]}
      />

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Filters and Actions */}
        <div className="border-b border-border py-4">
          <div className="flex items-center justify-between gap-4">
            <WorkOrdersFilters
              searchTerm={searchTerm}
              priorityFilter={priorityFilter}
              jobTypeFilter={jobTypeFilter}
              workOrderStatusFilter={workOrderStatusFilter}
              mirPendingDirectionFilter={mirPendingDirectionFilter}
              onSearchTermChange={handleSearchTermChange}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
            />

            <div className="flex items-center gap-2">
              {clientConfig.features.requestMoreInfo &&
                selectedJobs.length === 1 &&
                selectedJobs[0].mir_pending_direction === 'inbound' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-amber-600 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950"
                    onClick={handleRequestInfo}
                  >
                    <HelpCircle className="w-4 h-4 mr-2" />
                    Reply
                  </Button>
                )}
              {clientConfig.features.requestMoreInfo &&
                selectedJobs.length === 1 &&
                selectedJobs[0].mir_pending_direction === 'outbound' && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 text-sm text-blue-700 dark:text-blue-300 whitespace-nowrap">
                    <ArrowUpRight className="w-4 h-4 shrink-0" />
                    Awaiting reply
                  </div>
                )}
              {clientConfig.features.requestMoreInfo &&
                selectedJobs.length === 1 &&
                selectedJobs[0].mir_pending_direction === null && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-amber-600 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950"
                    onClick={handleRequestInfo}
                  >
                    <HelpCircle className="w-4 h-4 mr-2" />
                    Request More Info
                  </Button>
                )}
              {clientConfig.features.bulkActions && (
                <BulkActionsBar
                  selectedJobs={selectedJobs}
                  onAccept={handleAccept}
                  onReject={handleReject}
                />
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          <div className="bg-card border-t border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox checked={allSelected} onCheckedChange={handleSelectAll} />
                  </TableHead>
                  <TableHead className="font-medium text-foreground">Job Name</TableHead>
                  <TableHead className="font-medium text-foreground">Job Type</TableHead>
                  <TableHead className="font-medium text-foreground">Priority</TableHead>
                  <TableHead className="font-medium text-foreground">Info Request</TableHead>
                  <TableHead className="font-medium text-foreground">Permit</TableHead>
                  <TableHead className="font-medium text-foreground">Location</TableHead>
                  <TableHead className="font-medium text-foreground">Duration</TableHead>
                  <TableHead className="font-medium text-foreground">Required By</TableHead>
                  <TableHead className="font-medium text-foreground">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order) => {
                  const isSelected = selectedOrders.has(String(order.id));

                  return (
                    <TableRow
                      key={order.id}
                      className="hover:bg-muted/50 cursor-pointer"
                      onClick={() => {
                        if (!isSelected && selectedOrders.size === 0) {
                          onNavigateToDetail?.(String(order.id));
                        }
                      }}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) =>
                            handleSelectOrder(String(order.id), checked as boolean)
                          }
                        />
                      </TableCell>
                      <TableCell className="font-medium text-foreground">
                        {String(order.name)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        <span>{order.job_category_name ?? order.name ?? ''}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {(order.priority_name === 'High' ||
                            order.priority_name === 'Critical') && (
                            <AlertCircle className="w-4 h-4 text-red-600" />
                          )}
                          <Badge
                            variant="outline"
                            className={priorityColors[(order.priority_name ?? 'Low') as Priority]}
                          >
                            {order.priority_name ?? 'Unknown'}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        {order.mir_pending_direction === 'inbound' && (
                          <Badge
                            variant="outline"
                            className="text-amber-600 border-amber-600 gap-1"
                          >
                            <ArrowDownLeft className="w-3 h-3" />
                            Reply Needed
                          </Badge>
                        )}
                        {order.mir_pending_direction === 'outbound' && (
                          <Badge variant="outline" className="text-blue-600 border-blue-600 gap-1">
                            <ArrowUpRight className="w-3 h-3" />
                            Awaiting Reply
                          </Badge>
                        )}
                        {order.mir_pending_direction === null && (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {order.permit_required ||
                        order.reinstatement_required ||
                        order.permit_status_name ? (
                          <div className="flex flex-col items-start gap-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {order.permit_required && (
                                <Badge
                                  variant="outline"
                                  className="text-orange-600 border-orange-600 gap-1"
                                >
                                  <ShieldCheck className="size-3" />
                                  Permit
                                </Badge>
                              )}
                              {order.reinstatement_required && (
                                <Badge variant="outline" className="text-blue-600 border-blue-600">
                                  Reinstatement
                                </Badge>
                              )}
                            </div>
                            {order.permit_status_name && (
                              <span className="text-xs text-muted-foreground">
                                {order.permit_status_name}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-foreground">{order.address ?? ''}</TableCell>
                      <TableCell className="text-muted-foreground">—</TableCell>
                      <TableCell className="text-muted-foreground">
                        {order.req_completion_date
                          ? new Date(order.req_completion_date).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })
                          : '—'}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => onNavigateToDetail?.(String(order.id))}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {filteredOrders.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No jobs found</p>
              </div>
            )}
          </div>
        </div>

        {/* Pagination */}
        {totalCount > 0 && totalPages > 1 && (
          <div className="border-t border-border py-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * limit + 1} to{' '}
                {Math.min(currentPage * limit, totalCount)} of {totalCount} results
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    let pageNumber: number;
                    if (totalPages <= 7) {
                      pageNumber = i + 1;
                    } else if (currentPage <= 4) {
                      pageNumber = i + 1;
                    } else if (currentPage >= totalPages - 3) {
                      pageNumber = totalPages - 6 + i;
                    } else {
                      pageNumber = currentPage - 3 + i;
                    }

                    return (
                      <Button
                        key={pageNumber}
                        variant={currentPage === pageNumber ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentPage(pageNumber)}
                        className={currentPage === pageNumber ? 'text-white' : ''}
                        style={currentPage === pageNumber ? { backgroundColor: '#E84A2B' } : {}}
                      >
                        {pageNumber}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Single-job Request More Info / Reply modal */}
        <RequestMoreInfoOrReplyModal
          open={requestMoreInfoModalOpen}
          onOpenChange={setRequestMoreInfoModalOpen}
          job={selectedJobs.length === 1 ? selectedJobs[0] : null}
          isSubmitting={
            selectedJobs.length === 1 && selectedJobs[0].mir_pending_direction === 'inbound'
              ? replyMoreInfoWorkOrders.isPending
              : requestMoreInfoWorkOrders.isPending
          }
          onSubmit={(comment, files) => {
            if (selectedJobs.length !== 1) return;
            const filesMeta = files.map((f) => ({ name: f.name, size: f.size, type: f.type }));
            if (filesMeta.length > 0) {
              console.log('[request-more-info] attachments (not yet sent to backend):', filesMeta);
            }
            const payload = [
              {
                id: Number(selectedJobs[0].id),
                externalId: selectedJobs[0].external_id ?? '',
                comments: comment,
              },
            ];
            if (selectedJobs[0].mir_pending_direction === 'inbound') {
              replyMoreInfoWorkOrders.mutate(payload, {
                onSuccess: (resp) => {
                  setRequestMoreInfoModalOpen(false);
                  handleBulkResult(resp, 'Reply sent');
                  refetchJobList();
                },
                onError: (error) => {
                  setRequestMoreInfoModalOpen(false);
                  const message =
                    error instanceof Error ? error.message : 'An unexpected error occurred';
                  handleBulkResult(
                    { results: [{ id: selectedJobs[0].id, success: false, message }] },
                    'Reply sent',
                  );
                },
              });
            } else {
              requestMoreInfoWorkOrders.mutate(payload, {
                onSuccess: (resp) => {
                  setRequestMoreInfoModalOpen(false);
                  handleBulkResult(resp, 'Info request sent');
                  refetchJobList();
                },
                onError: (error) => {
                  setRequestMoreInfoModalOpen(false);
                  const message =
                    error instanceof Error ? error.message : 'An unexpected error occurred';
                  handleBulkResult(
                    { results: [{ id: selectedJobs[0].id, success: false, message }] },
                    'Info request sent',
                  );
                },
              });
            }
          }}
        />

        {/* Per-job stepper modal — Reject only. */}
        <BulkActionStepperModal
          open={stepperMode !== null}
          onOpenChange={(open) => {
            if (!open) setStepperMode(null);
          }}
          mode={stepperMode ?? 'reject'}
          jobs={selectedJobs.map((j) => ({
            id: j.id,
            name: j.name,
            address: j.address,
            work_order_status: j.work_order_status,
            req_completion_date: j.req_completion_date,
          }))}
          isSubmitting={rejectWorkOrders.isPending}
          onSubmit={(items: BulkActionStepperSubmitItem[]) => {
            // TODO(bulk-attachments): attachments are captured in the stepper
            // but not yet uploaded to S3 or included in the reject/RMI payload.
            // Log them for now so the flow is visible in dev; wire up presigned
            // uploads + the `attachments` field on the backend body when the
            // endpoint supports it.
            const filesByJob = items
              .filter((it) => it.files.length > 0)
              .map((it) => ({
                id: it.id,
                files: it.files.map((f) => ({ name: f.name, size: f.size, type: f.type })),
              }));
            if (filesByJob.length > 0) {
              console.log('[bulk-action] attachments (not yet sent to backend):', filesByJob);
            }

            const payload: BulkJobInput[] = items.map(({ id, comments }) => {
              const job = selectedJobs.find((j) => j.id === id);
              return { id, externalId: job?.external_id ?? '', comments };
            });

            if (stepperMode === 'reject') {
              rejectWorkOrders.mutate(payload, {
                onSuccess: (resp) => {
                  handleBulkResult(resp, 'Rejected');
                  refetchJobList();
                },
              });
            }
          }}
        />

        {/* Accept confirmation — no per-job input required, so a plain
            are-you-sure dialog is enough. */}
        <Dialog open={acceptModalOpen} onOpenChange={setAcceptModalOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>
                Accept {selectedOrders.size} job{selectedOrders.size === 1 ? '' : 's'}?
              </DialogTitle>
              <DialogDescription>
                This moves the selected jobs out of the "new" status.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setAcceptModalOpen(false)}
                disabled={acceptWorkOrders.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="text-white"
                style={{ backgroundColor: '#E84A2B' }}
                disabled={acceptWorkOrders.isPending}
                onClick={() => {
                  const ids = selectedJobs.map((j) => Number(j.id));
                  acceptWorkOrders.mutate(ids, {
                    onSuccess: (resp) => {
                      handleBulkResult(resp, 'Accepted');
                      // Belt-and-braces refetch: the mutation also invalidates
                      // the list query, but an explicit refetch here makes the
                      // UI update deterministic right after the modal closes.
                      refetchJobList();
                    },
                  });
                }}
              >
                {acceptWorkOrders.isPending ? 'Accepting…' : 'Accept'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Partial-failure result modal — only opened by handleBulkResult when
            the response contains at least one failed entry. */}
        {resultModalState.open && (
          <BulkActionResultModal
            open={resultModalState.open}
            onOpenChange={(open) => {
              if (!open) setResultModalState({ open: false });
            }}
            actionLabel={resultModalState.actionLabel}
            results={resultModalState.results}
            jobsLookup={resultModalState.jobsLookup}
          />
        )}
      </div>
    </div>
  );
}
